import React, { useEffect, useRef } from 'react';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';

const ChatInput = ({
	postContent,
	setPostContent,
	handleRecordClick,
	handleCancelRecording,
	isRecording,
	isTranscribing,
	submitMessageHandler,
	audioAnalyser,
	disabled = false,
}) => {
	const canvasRef = useRef(null);
	const dataHistoryRef = useRef([]);

	// Determines if we are in "Audio Mode" (either recording or processing)
	const isAudioMode = isRecording || isTranscribing;

	useEffect(() => {
		if (!isRecording || !audioAnalyser || !canvasRef.current) return;

		// Reset history when recording starts
		dataHistoryRef.current = [];

		const canvas = canvasRef.current;
		const ctx = canvas.getContext('2d');
		let animationId;

		const draw = () => {
			// Update canvas dimensions to match display size
			if (canvas.width !== canvas.clientWidth || canvas.height !== canvas.clientHeight) {
				canvas.width = canvas.clientWidth;
				canvas.height = canvas.clientHeight;
			}

			const bufferLength = audioAnalyser.frequencyBinCount;
			const dataArray = new Uint8Array(bufferLength);
			audioAnalyser.getByteTimeDomainData(dataArray);

			// Calculate RMS amplitude for this frame
			let sum = 0;
			for (let i = 0; i < bufferLength; i++) {
				const v = (dataArray[i] - 128) / 128.0;
				sum += v * v;
			}
			const rms = Math.sqrt(sum / bufferLength);

			// Calculate bar dimensions
			const rightPadding = 88; // Space for buttons
			const effectiveWidth = Math.max(0, canvas.width - rightPadding);
			const barWidth = 4;
			const gap = 3;
			const totalBarWidth = barWidth + gap;
			const maxBars = Math.floor(effectiveWidth / totalBarWidth);

			// Update history
			dataHistoryRef.current.push(rms);
			while (dataHistoryRef.current.length > maxBars) {
				dataHistoryRef.current.shift();
			}

			ctx.clearRect(0, 0, canvas.width, canvas.height);

			// Draw bars
			ctx.fillStyle = '#aaaaaa';

			const historyLength = dataHistoryRef.current.length;

			for (let i = 0; i < historyLength; i++) {
				const value = dataHistoryRef.current[i];

				// Scale height based on RMS
				// Multiply by a factor to make it visible, clamp to min/max
				let height = value * canvas.height * 10;
				height = Math.min(canvas.height, Math.max(4, height));

				// Calculate x position:
				// Align to the right side of effective area
				// Newest (last index) is on the right
				const x = effectiveWidth - (historyLength - i) * totalBarWidth;
				const y = (canvas.height - height) / 2;

				ctx.beginPath();
				if (ctx.roundRect) {
					ctx.roundRect(x, y, barWidth, height, 4);
				} else {
					ctx.rect(x, y, barWidth, height);
				}
				ctx.fill();
			}

			animationId = requestAnimationFrame(draw);
		};

		draw();

		return () => {
			cancelAnimationFrame(animationId);
		};
	}, [isRecording, audioAnalyser]);

	return isAudioMode ? (
		<Box style={{ padding: '1rem 0.75rem', position: 'relative', overflow: 'hidden' }}>
			<canvas
				ref={canvasRef}
				style={{
					width: '100%',
					height: '3.25rem',
					padding: '0.5rem',
					border: '1px solid red',
					borderRadius: '4px',
					zIndex: 10,
					pointerEvents: 'none',
					backgroundColor: 'rgba(255, 255, 255, 0.9)', // Fade out the background
					borderRadius: '4px',
				}}
			/>
			<Box
				sx={{
					position: 'absolute',
					bottom: '1.4rem',
					right: '1.25rem',
				}}
			>
				<IconButton
					onClick={handleCancelRecording}
					aria-label="Cancel recording"
					color="error"
					style={{ zIndex: 20 }}
					disabled={isTranscribing}
				>
					<CloseIcon />
				</IconButton>
				<IconButton
					onClick={isTranscribing ? undefined : handleRecordClick}
					aria-label={isTranscribing ? 'Transcribing' : 'Submit recording'}
					color="primary"
					style={{ zIndex: 20 }}
					disabled={isTranscribing}
				>
					{isTranscribing ? <CircularProgress size={24} /> : <CheckIcon />}
				</IconButton>
			</Box>
		</Box>
	) : (
		<Box style={{ padding: '1rem 0.75rem', position: 'relative', overflow: 'hidden' }}>
			<TextField
				sx={{
					'& .MuiInputBase-root': {
						paddingRight: '5.5rem', // space for icons
						fontSize: '0.85rem',
					},
					'& .MuiInputLabel-root': {
						fontSize: '0.85rem',
					},
				}}
				label={
					isTranscribing ? 'Transcribing...' : isRecording ? 'Listening...' : 'How may I help you with your dataspace?'
				}
				minRows={1}
				maxRows={Infinity}
				multiline
				value={postContent}
				onChange={(e) => setPostContent(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === 'Enter' && !e.shiftKey) {
						e.preventDefault();
						if (!disabled && !isTranscribing) {
							submitMessageHandler();
						}
					}
				}}
				variant="outlined"
				fullWidth
				aria-label="Chat input"
				disabled={disabled || isTranscribing}
			/>

			<Box
				sx={{
					position: 'absolute',
					bottom: '1.4rem',
					right: '1.25rem',
				}}
			>
				<IconButton onClick={handleRecordClick} aria-label="Record audio" color="default" disabled={disabled}>
					<MicIcon />
				</IconButton>
				<IconButton
					onClick={submitMessageHandler}
					className="send-button"
					aria-label="Send message"
					disabled={disabled || isTranscribing}
				>
					<SendIcon />
				</IconButton>
			</Box>
		</Box>
	);
};

export default ChatInput;
